import axios from 'axios';
import api from '../utils/apiInterceptor';

// 根据环境自动切换API基础URL
const isDevelopment = process.env.NODE_ENV === 'development';
const currentHost = window.location.hostname;

// 自动检测部署环境
const getApiBaseUrl = () => {
  // 如果通过环境变量明确设置了API URL，则使用它
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 开发服务器（react-scripts start，端口3000）或本地直连时，跨域调用本地后端
  if (isDevelopment || currentHost === 'localhost') {
    return 'http://localhost:5000/api';
  }

  // 生产环境：同源相对路径，自动适配任何域名/IP/协议（nginx 统一代理 /api 到后端）
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// 生产环境不输出调试信息

// 全局设置：强制禁用模拟数据
export const ENABLE_MOCK_DATA = false;

// 🔥【QA问题3修复 2026-09-01】统一HTTP封装：删除本文件私有的 axios 实例（apiInstance）及其重复拦截器，
// 全部请求统一走 utils/apiInterceptor.js 的共享实例（64处调用本已使用该实例，现收敛为唯一实例）。
// 共享实例能力覆盖：token注入、会话过期处理、401清理、批量生成超时30分钟、429提示、错误归一化(userMessage/serverMessage)。

// 检查服务器状态 - 直接使用健康检查端点
const checkServerStatus = async () => {
  try {
    // 同源相对路径或本地后端（由 getApiBaseUrl 推导健康检查基地址）
    const baseUrl = getApiBaseUrl().replace('/api', '');
    
    // 使用/health而不是/api/health
    const response = await axios.get(`${baseUrl}/health`, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      timeout: 5000
    });
    
    // 只要能够获取响应，就认为服务器是可用的
    return { 
      status: 'ok', 
      message: '服务器运行正常',
      details: response.data
    };
  } catch (error) {
    // 如果服务器不可达，添加标记以便UI显示更友好的错误
    error.isConnectionError = !error.response;
    throw error;
  }
};

// 获取用户信息
const getUserInfo = async () => {
  try {
    const response = await api.get('/auth/me');
    
    // 处理API返回的嵌套数据格式
    let userData = response.data;
    
    // 如果返回的是{success: true, user: {...}}格式
    if (userData.success && userData.user) {
      userData = userData.user;
      // 修改response.data使其与后续代码兼容
      response.data = userData;
    }
    
    // 确保返回数据包含余额字段
    if (response.data && (typeof response.data.balance === 'undefined' || response.data.balance === null)) {
      // 尝试从本地存储获取完整用户数据
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const storedUserData = JSON.parse(storedUser);
          if (storedUserData && typeof storedUserData.balance !== 'undefined' && storedUserData.balance !== null) {
            // 补充余额字段
            response.data.balance = storedUserData.balance;
          }
        } catch (e) {
          // 静默处理解析错误
        }
      }
      
      // 如果仍然没有余额，设置默认值为0
      if (typeof response.data.balance === 'undefined' || response.data.balance === null) {
        response.data.balance = 0;
      }
    }
    
    // 确保返回数据包含totalGenerated字段
    if (response.data && (typeof response.data.totalGenerated === 'undefined' || response.data.totalGenerated === null)) {
      // 尝试从本地存储获取
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const storedUserData = JSON.parse(storedUser);
          if (storedUserData && typeof storedUserData.totalGenerated !== 'undefined' && storedUserData.totalGenerated !== null) {
            // 补充totalGenerated字段
            response.data.totalGenerated = storedUserData.totalGenerated;
          }
        } catch (e) {
          // 静默处理解析错误
        }
      }
      
      // 如果仍然没有totalGenerated，设置默认值为0
      if (typeof response.data.totalGenerated === 'undefined' || response.data.totalGenerated === null) {
        response.data.totalGenerated = 0;
      }
    }
    
    // 确保数值类型正确
    if (response.data) {
      // 确保余额是数字类型
      if (response.data.balance !== undefined) {
        response.data.balance = Number(response.data.balance);
      }
      
      // 确保totalGenerated是数字类型
      if (response.data.totalGenerated !== undefined) {
        response.data.totalGenerated = Number(response.data.totalGenerated);
      }
    }
    
    // 确保本地存储的数据是最新的
    localStorage.setItem('user', JSON.stringify(response.data));
    
    return response;
  } catch (error) {
    // 尝试从本地存储获取用户数据
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const storedUserData = JSON.parse(storedUser);
        return { 
          data: storedUserData,
          status: 200,
          statusText: 'OK (from localStorage)'
        };
      } catch (e) {
        // 静默处理解析错误
      }
    }
    
    throw error;
  }
};

// 更新用户资料（包括教程进度）
const updateProfile = async (profileData) => {
  try {
    const response = await api.put('/auth/profile', profileData);
    
    // 更新本地存储
    if (response.data && response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

// 用户登录
const getCaptcha = async () => {
  const response = await api.get('/auth/captcha');
  return response;
};

// 识别账号绑定手机号（返回掩码手机号，供用户补全中间4位确认身份）
const detectForgotAccount = async (identifier) => {
  const response = await api.post('/auth/forgot-password/detect', { username: identifier });
  return response;
};

// 发送找回密码短信验证码（按用户名找回时需携带 phoneMiddle=手机号中间4位）
const sendForgotSmsCode = async (identifier, phoneMiddle) => {
  const response = await api.post('/auth/forgot-password/send-code', {
    username: identifier,
    phoneMiddle: phoneMiddle || undefined,
  });
  return response;
};

// 短信验证码重置密码
const resetPasswordBySms = async ({ identifier, code, newPassword }) => {
  const response = await api.post('/auth/forgot-password/reset', { username: identifier, code, newPassword });
  return response;
};

const login = async (credentials) => {
  try {
    const { username, password, rememberMe } = credentials;
    
    // 参数验证
    if (!username || !password) {
      throw new Error('用户名和密码不能为空');
    }

    // 登录不需验证码
    const response = await api.post('/auth/login', { username, password, rememberMe });
    
    // 验证返回的数据
    if (!response.data) {
      throw new Error('登录失败：服务器未返回数据');
    }
    
    if (!response.data.token) {
      throw new Error('登录失败：服务器未返回token');
    }
    
    return response;
  } catch (error) {
    // 保存详细错误信息，便于前端组件处理
    if (!error.response) {
      error.userMessage = '无法连接到服务器，请检查网络或服务器状态';
    } else if (error.response.status === 400) {
      // 优先显示服务端返回的具体错误（如验证码错误）
      error.userMessage = error.response.data?.message || '用户名或密码格式不正确';
    } else if (error.response.status === 401) {
      error.userMessage = error.response.data?.message || '用户名或密码错误';
    } else if (error.response.status === 500) {
      error.userMessage = '服务器内部错误，请稍后再试';
    } else {
      error.userMessage = error.response.data?.message || '登录过程中发生错误';
    }
    
    throw error;
  }
};

// 用户注册
const register = async (userData) => {
  try {
    const { username, email, phone, password, captchaId, captchaCode } = userData;
    
    // 参数验证
    if (!username || !password) {
      throw new Error('用户名和密码不能为空');
    }
    
    // 至少要有邮箱或手机号其中一个
    if (!email && !phone) {
      throw new Error('邮箱和手机号至少要提供一个');
    }
    
    // 只发送非空的字段，避免发送 undefined 导致数据库索引冲突
    const requestData = { username, password, captchaId, captchaCode };
    if (email) requestData.email = email;
    if (phone) requestData.phone = phone;
    if (userData.inviteCode) requestData.inviteCode = userData.inviteCode;
    
    const response = await api.post('/auth/register', requestData);
    
    // 验证返回的数据
    if (!response.data) {
      throw new Error('注册失败：服务器未返回数据');
    }
    
    return response;
  } catch (error) {
    // 保存详细错误信息，便于前端组件处理
    if (!error.response) {
      error.userMessage = '无法连接到服务器，请检查网络或服务器状态';
    } else if (error.response.status === 400) {
      // 400错误通常包含具体的验证错误信息
      error.userMessage = error.response.data?.message || '注册信息格式不正确，请检查输入';
    } else if (error.response.status === 409) {
      // 409冲突，通常是用户名或邮箱已存在
      error.userMessage = error.response.data?.message || '用户名或邮箱已被使用';
    } else if (error.response.status === 500) {
      error.userMessage = '服务器内部错误，请稍后再试';
    } else {
      error.userMessage = error.response.data?.message || '注册过程中发生错误，请稍后再试';
    }
    
    throw error;
  }
};

// 用户登出
const logout = async () => {
  try {
    const response = await api.post('/auth/logout');
    
    // 清除本地存储的认证信息
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionId');
    
    // 触发用户数据更新事件
    window.dispatchEvent(new CustomEvent('userLoggedOut'));
    
    return response;
  } catch (error) {
    // 即使服务器登出失败，也要清除本地存储
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionId');
    
    // 触发用户数据更新事件
    window.dispatchEvent(new CustomEvent('userLoggedOut'));
    
    throw error;
  }
};

// 强制用户下线（管理员功能）
const forceLogoutUser = async (userId) => {
  try {
    const response = await api.post(`/auth/force-logout/${userId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

// 管理员修改用户密码
const changeUserPassword = async (userId, newPassword) => {
  try {
    const response = await api.post(`/kOjtx3AXNmCsT5Wz/users/${userId}/change-password`, { 
      newPassword 
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 用户修改自己的密码
const changePassword = async (oldPassword, newPassword) => {
  try {
    const response = await api.post('/auth/change-password', { 
      oldPassword, 
      newPassword 
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 获取系统统计数据
const getSystemStats = async (timeRange = '7d') => {
  try {
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/stats?timeRange=${timeRange}`);
    
    // 检查响应的数据结构
    if (response.data && response.data.success && response.data.stats) {
      // 后端返回 { success: true, stats: { ... } } 的结构
      return response.data.stats;
    } else {
      // 如果不是预期的结构，直接返回整个data对象
      return response.data;
    }
  } catch (error) {
    throw error;
  }
};

// 管理仪表盘 - 概览
const getAdminDashboardOverview = async (days = 7) => {
  try {
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/dashboard/overview`, {
      params: { days }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理仪表盘 - 趋势数据
const getAdminDashboardTrends = async (days = 14) => {
  try {
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/dashboard/trends`, {
      params: { days }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理仪表盘 - 用户分段
const getAdminUserSegments = async () => {
  try {
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/dashboard/user-segments`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理仪表盘 - 榜单
const getAdminTopUsers = async (limit = 5) => {
  try {
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/dashboard/top-users`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理仪表盘 - 用户洞察
const getAdminUserInsights = async (userId) => {
  if (!userId) {
    throw new Error('用户ID不能为空');
  }
  try {
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/users/${userId}/insights`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理仪表盘 - 异常监控
const getAdminUserAnomalies = async (days = 30) => {
  try {
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/dashboard/anomalies`, {
      params: { days }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 批量重置所有小说的重写状态（管理员专用）
const resetAllRewritingStatus = async () => {
  try {
    const response = await api.post('/kOjtx3AXNmCsT5Wz/novels/reset-all-rewriting-status');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理员 - 获取扫码充值订单列表
const getAdminRechargeOrders = async (params = {}) => {
  try {
    const response = await api.get('/kOjtx3AXNmCsT5Wz/recharge/orders', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理员 - 获取卡密充值记录
const getAdminCardKeyRecharges = async (params = {}) => {
  try {
    const response = await api.get('/kOjtx3AXNmCsT5Wz/recharge/card-key-records', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理员 - 获取所有充值记录汇总（包含扫码和卡密）
const getAdminAllRechargeRecords = async (params = {}) => {
  try {
    const response = await api.get('/kOjtx3AXNmCsT5Wz/recharge/all-records', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 管理员 - 获取充值统计概览
const getAdminRechargeStats = async (params = {}) => {
  try {
    const response = await api.get('/kOjtx3AXNmCsT5Wz/recharge/stats', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 获取所有用户
const getAllUsers = async (page = 1, limit = 10, search = '', extraParams = {}) => {
  try {
    let options = {};

    if (typeof page === 'object' && page !== null) {
      options = { ...page };
    } else if (typeof search === 'object' && search !== null) {
      options = {
        page,
        limit,
        ...(search || {})
      };
    } else {
      options = {
        page,
        limit,
        search,
        ...(extraParams || {})
      };
    }

    const queryParams = new URLSearchParams();
    const appendParam = (key, value) => {
      if (value === undefined || value === null) {
        return;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '') {
          queryParams.append(key, trimmed);
        }
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(item => appendParam(key, item));
        return;
      }
      queryParams.append(key, value);
    };

    Object.entries(options).forEach(([key, value]) => {
      if (key === 'search' && typeof value === 'string' && value.trim() === '') {
        return;
      }
      appendParam(key, value);
    });

    if (!queryParams.has('page')) {
      queryParams.append('page', 1);
    }
    if (!queryParams.has('limit')) {
      queryParams.append('limit', 10);
    }

    const queryString = queryParams.toString();
    const response = await api.get(`/kOjtx3AXNmCsT5Wz/users${queryString ? `?${queryString}` : ''}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 创建用户
const createUser = async (userData) => {
  try {
    const response = await api.post('/kOjtx3AXNmCsT5Wz/users', userData);
    return response.data; // 直接返回data对象
  } catch (error) {
    throw error;
  }
};

// 更新用户
const updateUser = async (userId, userData) => {
  try {
    const response = await api.put(`/kOjtx3AXNmCsT5Wz/users/${userId}`, userData);
    return response.data; // 直接返回data对象
  } catch (error) {
    throw error;
  }
};

// 删除用户
const deleteUser = async (userId) => {
  try {
    const response = await api.delete(`/kOjtx3AXNmCsT5Wz/users/${userId}`);
    return response.data; // 直接返回data对象
  } catch (error) {
    throw error;
  }
};

// 重置用户密码为默认值
const resetUserPassword = async (userId) => {
  if (!userId) {
    throw new Error('用户ID不能为空');
  }
  try {
    const response = await api.post(`/kOjtx3AXNmCsT5Wz/users/${userId}/reset-password`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 获取卡密列表
const getCardKeys = async () => {
  try {
    const response = await api.get('/kOjtx3AXNmCsT5Wz/card-keys');
    return response.data; // 直接返回data对象
  } catch (error) {
    throw error;
  }
};

// 获取全部卡密列表（带分页和搜索）
const getAllCardKeys = async (...args) => {
  try {
    let options = {};

    if (args.length === 0) {
      options = {};
    } else if (args.length === 1 && typeof args[0] === 'object') {
      options = args[0] || {};
    } else {
      const [page = 1, limit = 10, status, search] = args;
      options = { page, limit, status, search };
    }

    const {
      page = 1,
      limit = 10,
      status,
      search,
      type,
      amountRange,
      expiryFilter,
      sort = 'createdAt',
      order = 'desc'
    } = options;

    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);

    if (status) {
      params.set('status', status);
    }

    if (search && search.trim()) {
      params.set('search', search.trim());
    }

    if (type && type !== 'all') {
      params.set('type', type);
    }

    if (amountRange && amountRange !== 'all') {
      params.set('amountRange', amountRange);
    }

    if (expiryFilter && expiryFilter !== 'all') {
      params.set('expiryFilter', expiryFilter);
    }

    if (sort) {
      params.set('sort', sort);
    }

    if (order) {
      params.set('order', order);
    }

    const response = await api.get(`/kOjtx3AXNmCsT5Wz/card-keys?${params.toString()}`);
    return response.data; // 直接返回data对象
  } catch (error) {
    throw error;
  }
};

// 生成卡密
const generateCardKeys = async (count, amount, type = 'standard', remarks = '') => {
  try {
    // 确保amount是数字类型
    const parsedAmount = Number(amount);
    const parsedCount = Number(count);
    
    // 如果是测试卡，固定金额为30000
    const isTestCard = type === 'test';
    let finalAmount = parsedAmount;
    
    if (!isTestCard) {
      // 验证amount是否在预定义金额范围内（仅对普通卡密）
    const validAmounts = [10000, 50000, 100000, 300000, 500000, 1000000, 5000000, 10000000];
    let closestAmount = validAmounts[0];
    let isExactMatch = false;
    
    for (const valid of validAmounts) {
      if (Math.abs(parsedAmount - valid) < 0.1) {
        isExactMatch = true;
        closestAmount = valid;
        break;
      }
      
      if (Math.abs(parsedAmount - valid) < Math.abs(parsedAmount - closestAmount)) {
        closestAmount = valid;
      }
    }
    
    // 如果不是精确匹配但很接近，使用标准金额
      finalAmount = isExactMatch ? parsedAmount : closestAmount;
    } else {
      // 测试卡固定金额为30000
      finalAmount = 30000;
    }
    
    // 准备请求参数，确保包含必需的字段
    const requestData = {
      count: parsedCount,
      amount: finalAmount,  // 使用纠正后的金额
      type: type || 'standard',
      expiryDays: 365, // 默认有效期365天
      remarks: remarks || '',
      batchName: `批次-${new Date().toISOString().split('T')[0]}`
    };
    
    const response = await api.post('/kOjtx3AXNmCsT5Wz/card-keys', requestData);
    return response.data; // 直接返回data对象
  } catch (error) {
    throw error;
  }
};

// 删除卡密
const deleteCardKey = async (id) => {
  try {
    const response = await api.delete(`/kOjtx3AXNmCsT5Wz/card-keys/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 充值后获取最新信息并更新状态
const updateAfterRecharge = async (successResponse) => {
  try {
    // 保存卡密预期金额，用于后续验证
    const expectedAmount = successResponse.data.amount || 0;
    localStorage.setItem('lastRechargeAmount', expectedAmount.toString());
    
    // 同时获取最新用户信息和充值记录
    const [userInfoResponse] = await Promise.allSettled([
      getUserInfo(),
      getUserRecharges()
    ]);
    
    // 处理用户信息更新
    if (userInfoResponse.status === 'fulfilled' && userInfoResponse.value && userInfoResponse.value.data) {
      let userData = userInfoResponse.value.data;
      
      // 处理可能的嵌套格式
      if (userData.success && userData.user) {
        userData = userData.user;
      }
      
      // 获取充值前余额
      const beforeUser = localStorage.getItem('userBeforeRecharge');
      let oldBalance = 0;
      if (beforeUser) {
        try {
          const oldUserData = JSON.parse(beforeUser);
          oldBalance = oldUserData.balance || 0;
        } catch (e) {
          // 静默处理解析错误
        }
      }
      
      // 计算实际增加的余额
      const actualIncrease = userData.balance - oldBalance;
      
      // 修改响应，添加预期金额和实际增加金额
      successResponse.data.expectedAmount = expectedAmount;
      successResponse.data.actualAmount = actualIncrease;
      
      // 触发用户数据更新事件
      window.dispatchEvent(new CustomEvent('userDataUpdated', {
        detail: { 
          userData: userData,
          rechargeInfo: {
            expectedAmount,
            actualAmount: actualIncrease
          },
          suppressInfoUpdate: true, // 抑制通用更新提示
          source: 'recharge' // 标记来源是充值操作
        }
      }));
    }
    
    // 处理充值记录更新（由组件自己在刷新时获取）
    
    return successResponse; // 返回原始的成功响应
  } catch (error) {
    return successResponse; // 即使更新失败，也返回原始的成功响应
  }
};

// 用户充值
const recharge = async (cardKey) => {
  try {
    const beforeUser = localStorage.getItem('user');
    // 保存充值前的用户数据
    localStorage.setItem('userBeforeRecharge', beforeUser);
    
    // 发送充值请求到后端 - 修正API路径
    const response = await api.post('/recharge/card-key', { cardKey });
    
    // 处理API响应
    if (response.data.success) {
      // 获取卡密金额，用于检测充值是否正确
      const expectedAmount = response.data.amount || 0;
      
      // 保存预期金额到localStorage，用于后续验证
      localStorage.setItem('lastRechargeAmount', expectedAmount.toString());
      
      // 获取最新信息并更新状态
      return await updateAfterRecharge(response);
    } else {
      // 明确的失败响应，返回具体错误消息
      
      // 根据错误类型返回更具体的错误信息
      let errorMessage = response.data.message || '充值处理失败';
      
      // 处理特殊错误类型
      if (response.status === 404) {
        errorMessage = '卡密不存在，请检查输入是否正确';
      } else if (response.status === 400 && errorMessage.includes('已被使用')) {
        errorMessage = '此卡密已被使用，请使用新的卡密';
      }
      
      // 构造一个包含明确错误信息的响应对象
      return {
        status: response.status,
        data: {
          success: false,
          message: errorMessage,
          errorType: response.status === 404 ? 'not_found' : 
                   (response.status === 400 ? 'invalid_card' : 'server_error')
        }
      };
    }
  } catch (error) {
    // 检查是否有具体错误响应
    if (error.response && error.response.data) {
      // 从错误响应中提取消息
      let errorMessage = error.response.data.message || '充值失败，请稍后再试';
      let errorType = 'unknown';
      
      // 根据状态码和消息内容进一步细化错误类型
      if (error.response.status === 404) {
        errorMessage = '卡密不存在，请检查输入是否正确';
        errorType = 'not_found';
      } else if (error.response.status === 400) {
        // 测试卡重复使用（优先检查，因为消息也包含"已使用"或"使用过"）
        if (errorMessage.includes('测试卡') &&
            (errorMessage.includes('已使用') || errorMessage.includes('使用过'))) {
          // 保持后端返回的原始消息
          errorType = 'test_card_used';
          // 不覆盖原始消息，让用户看到具体原因
        } else if (errorMessage.includes('体验卡')) {
          // 体验卡相关错误，保持原始消息
          errorType = 'experience_card_limited';
          // 不覆盖原始消息
        } else if (errorMessage.includes('已被使用')) {
          errorMessage = '此卡密已被使用，请使用新的卡密';
          errorType = 'used_card';
        } else {
          errorType = 'invalid_request';
        }
      } else if (error.response.status === 401) {
        errorMessage = '登录已过期，请重新登录';
        errorType = 'unauthorized';
      }
      
      // 返回一个包含明确错误信息的响应对象
      return {
        status: error.response.status,
        data: {
          success: false,
          message: errorMessage,
          errorType: errorType
        }
      };
    }
    
    // 先检查是否是服务器500错误
    const isServerError = error.response?.status === 500;
    
    // 无论如何尝试获取最新的用户信息，因为可能充值已经成功
    try {
      // 获取一次最新的用户信息
      const userResponse = await getUserInfo();
      
      // 检查余额是否比之前高
      const oldUserStr = localStorage.getItem('userBeforeRecharge') || localStorage.getItem('user');
      if (oldUserStr) {
        try {
          const oldUser = JSON.parse(oldUserStr);
          const newBalance = userResponse.data.balance;
          const oldBalance = oldUser.balance;
          
          if (newBalance > oldBalance) {
            const increasedAmount = newBalance - oldBalance;
            
            // 获取预期金额
            const expectedAmount = localStorage.getItem('lastRechargeAmount') || 0;
            
            // 创建一个成功的响应对象
            const successResponse = {
              status: 200,
              data: {
                success: true,
                message: '充值成功，余额已更新',
                newBalance: newBalance,
                amount: increasedAmount, // 使用实际增加的金额
                expectedAmount: expectedAmount // 添加预期金额
              }
            };
            
            // 获取最新信息并更新状态
            return await updateAfterRecharge(successResponse);
          }
        } catch (e) {
          // 静默处理解析错误
        }
      }
    } catch (checkError) {
      // 静默处理检查错误
    }
    
    // 如果是服务器500错误，返回特殊对象而不是抛出异常
    if (isServerError) {
      // 返回一个特殊的响应对象，表示需要前端组件进行进一步验证
      return {
        status: 202, // 202 Accepted - 表示请求已接受，但处理未完成
        data: {
          needsVerification: true,
          message: '充值请求已发送，正在验证处理结果'
        }
      };
    }
    
    // 返回一个带有友好错误信息的响应
    return {
      status: error.response?.status || 500,
      data: {
        success: false,
        message: error.message || '充值失败，请稍后再试',
        errorType: 'network_error'
      }
    };
  }
};

// 获取用户的充值记录
const getUserRecharges = async () => {
  try {
    const response = await api.get('/recharge/transactions/recharge');
    
    // 处理响应数据
    if (response && response.data) {
      return { data: response.data };
    } else if (Array.isArray(response)) {
      return { data: response };
    }
    
    return { data: [] };
  } catch (error) {
    // 返回空数组，避免前端崩溃
    return { data: [] };
  }
};

// 获取用户的所有交易记录
const getUserTransactions = async () => {
  try {
    const response = await api.get('/recharge/transactions');
    
    // 处理响应数据
    if (response && response.data) {
      return { data: response.data };
    } else if (Array.isArray(response)) {
      return { data: response };
    }
    
    return { data: [] };
  } catch (error) {
    // 返回空数组，避免前端崩溃
    return { data: [] };
  }
};

// 获取用户的消费记录
const getUserConsumptions = async () => {
  try {
    const response = await api.get('/recharge/transactions/consumption');
    
    // 处理响应数据
    if (response && response.data) {
      return { data: response.data };
    } else if (Array.isArray(response)) {
      return { data: response };
    }
    
    return { data: [] };
  } catch (error) {
    // 返回空数组，避免前端崩溃
    return { data: [] };
  }
};

// ==================== 支付宝当面付 ====================

// 获取充值套餐列表（复用微信的套餐接口）
const getAlipayPackages = async () => {
  try {
    const response = await api.get('/pay-recharge/packages');
    if (response && response.data) {
      return { success: true, data: response.data };
    }
    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

// 创建支付宝扫码支付订单
const createAlipayOrder = async (packageId) => {
  try {
    const response = await api.post('/alipay/create-order', { packageId });
    if (response && response.data) {
      return response.data;
    }
    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

// 查询支付宝订单状态
const queryAlipayOrder = async (orderNo) => {
  try {
    const response = await api.get(`/alipay/query-order?orderNo=${orderNo}`);
    if (response && response.data) {
      return response.data;
    }
    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

// 取消支付宝订单
const cancelAlipayOrder = async (orderNo) => {
  try {
    const response = await api.post('/alipay/cancel-order', { orderNo });
    if (response && response.data) {
      return response.data;
    }
    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

// 优化章节内容
const optimizeChapterContent = async (chapterInfo) => {
  // 使用传入的请求ID或生成新的
  const requestId = chapterInfo.requestId || Math.random().toString(36).substring(7);
  
  try {
    // 简化上下文，只传递前一章和后一章
    const requestData = {
      novelId: chapterInfo.novelId,
      chapterId: chapterInfo.chapterId,
      chapterTitle: chapterInfo.chapterTitle,
      content: chapterInfo.content,
      perspective: chapterInfo.perspective || '第三人称',
      style: chapterInfo.style || '写实风格',
      genre: chapterInfo.genre || '未指定',
      novelTitle: chapterInfo.novelTitle || '',
      prevChapter: chapterInfo.prevChapter || '',
      nextChapter: chapterInfo.nextChapter || '',
      novelOutline: chapterInfo.novelOutline || '',
      requestId: requestId
    };
    
    // 不添加自定义头部，避免CORS问题
    const response = await api.post('/ai/optimize-novel-chapter', requestData, {
      timeout: 180000 // 设置180秒超时
    });
    
    if (response.data) {
      // 验证响应是否与请求匹配
      // 确保响应中包含请求ID
      response.data.requestId = requestId;
    }
    
    return response.data;
  } catch (error) {
    // 根据错误类型返回不同的错误信息
    if (error.code === 'ECONNABORTED') {
      throw new Error('优化请求超时，请重试');
    } else if (error.response) {
      const errorMessage = error.response.data?.message || '优化失败，请重试';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('无法连接到服务器，请检查网络连接');
    } else {
      throw new Error('优化失败: ' + error.message);
    }
  }
};

// 异步优化章节内容（不等待完成）
const optimizeChapterContentAsync = (chapterInfo) => {
  const optimizePromise = optimizeChapterContent(chapterInfo);
  
    // 返回一个包含promise的对象，允许调用者选择是否等待
  return {
    promise: optimizePromise,
    // 添加取消功能（如果需要）
    cancel: () => {
      // 这里可以添加取消逻辑
    }
  };
};

// 🔥 同步重写章节内容（等待完成后返回）
const rewriteChapterContentSync = async (chapterInfo) => {
  try {
    const requestId = chapterInfo.requestId || `rewrite-sync-${Date.now().toString(36)}`;
    
    console.log(`[${requestId}] 发送同步重写请求:`, {
      novelId: chapterInfo.novelId,
      chapterId: chapterInfo.chapterId,
      chapterIndex: chapterInfo.chapterIndex
    });
    
    // 🔥 使用展开运算符，像章节优化一样传递所有参数
    const requestData = {
      ...chapterInfo,
      requestId: requestId
    };
    
    const response = await api.post('/ai/rewrite-chapter-sync', requestData, {
      timeout: 180000 // 180秒超时
    });
    
    if (response.data) {
      response.data.requestId = requestId;
    }
    
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('重写请求超时，请重试');
    } else if (error.response) {
      const errorMessage = error.response.data?.message || '重写失败，请重试';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('无法连接到服务器，请检查网络连接');
    } else {
      throw new Error('重写失败: ' + error.message);
    }
  }
};

// 异步重写章节内容（返回promise对象）
const rewriteChapterContentAsync = (chapterInfo) => {
  const rewritePromise = rewriteChapterContentSync(chapterInfo);
  
  return {
    promise: rewritePromise,
    cancel: () => {
      // 这里可以添加取消逻辑
    }
  };
};

// 安全刷新用户数据
// 🔥 并发去重 + 30秒节流：
//    - in-flight 去重：StrictMode/多组件同时挂载时，并发调用共享同一个请求
//      （此前 ProtectedRoute 双发 auth/me，页面加载阻塞 650ms×2）
//    - 30s 节流：短时间内重复调用直接复用上次成功结果，可传 {force:true} 强制刷新
let _refreshInFlight = null;
let _refreshLastOk = 0;
let _refreshLastPromise = null;
const REFRESH_THROTTLE_MS = 30000;

const refreshUserData = async (options = {}) => {
  // 节流窗口内直接复用上次结果（force 跳过）
  if (!options.force && _refreshLastPromise && Date.now() - _refreshLastOk < REFRESH_THROTTLE_MS) {
    return _refreshLastPromise;
  }
  // 已有进行中的请求：并发调用直接共享
  if (_refreshInFlight) return _refreshInFlight;

  _refreshInFlight = (async () => {
    try {
      const response = await getUserInfo();

      if (response && response.data) {
        // 获取用户数据
        let userData = response.data;

        // 处理数据嵌套情况
        if (userData.success && userData.user) {
          userData = userData.user;
        }

        // 确保余额是数字类型，防止显示为0的问题
        if (userData.balance !== undefined) {
          userData.balance = Number(userData.balance);
        }

        // 获取之前的用户数据进行比较
        let oldUserData = {};
        try {
          const oldUserStr = localStorage.getItem('user');
          if (oldUserStr) {
            oldUserData = JSON.parse(oldUserStr);
          }
        } catch (e) {
          // 静默处理解析错误
        }

        // 保存到localStorage
        localStorage.setItem('user', JSON.stringify(userData));

        // 通知UI更新用户数据
        window.dispatchEvent(new CustomEvent('userDataUpdated', {
          detail: {
            userData,
            source: options.source || 'api-refresh',
            oldBalance: oldUserData.balance || 0,
            newBalance: userData.balance || 0,
            suppressInfoUpdate: true // 始终抑制通用更新提示
          }
        }));
      }

      _refreshLastOk = Date.now();
      _refreshLastPromise = Promise.resolve(true);
      return true;
    } catch (error) {
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();

  return _refreshInFlight;
};

// 获取所有公告（管理员）
const getAnnouncements = async () => {
  try {
    const response = await api.get('/kOjtx3AXNmCsT5Wz/announcements');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 创建公告
const createAnnouncement = async (data) => {
  try {
    const response = await api.post('/kOjtx3AXNmCsT5Wz/announcements', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 更新公告
const updateAnnouncement = async (id, data) => {
  try {
    const response = await api.put(`/kOjtx3AXNmCsT5Wz/announcements/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 删除公告
const deleteAnnouncement = async (id) => {
  try {
    const response = await api.delete(`/kOjtx3AXNmCsT5Wz/announcements/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 获取我的邀请码
const getMyInviteCode = async () => {
  try {
    const response = await api.get('/invitation/my-code');
    return response;
  } catch (error) {
    throw error;
  }
};

// 获取邀请统计
const getInviteStats = async () => {
  try {
    const response = await api.get('/invitation/stats');
    return response;
  } catch (error) {
    throw error;
  }
};

// 获取邀请列表
const getInviteList = async (params = {}) => {
  try {
    const { status, page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    queryParams.append('page', page);
    queryParams.append('limit', limit);
    
    const response = await api.get(`/invitation/list?${queryParams.toString()}`);
    return response;
  } catch (error) {
    throw error;
  }
};

// 验证邀请码
const verifyInviteCode = async (code) => {
  try {
    const response = await api.get(`/invitation/verify/${code}`);
    return response;
  } catch (error) {
    throw error;
  }
};

// 获取内容生成权限状态（服务端判断）
const getAccessStatus = async () => {
  try {
    const response = await api.get('/auth/access-status');
    return response?.data || { success: false };
  } catch (error) {
    return { success: false };
  }
};

// ============================================================
// 【管理员 - 辅助创作 AI 次数管理】
// ============================================================

const getAssistAiQuotaList = async (params = {}) => {
  const { page = 1, limit = 20, search = '' } = params;
  const qs = new URLSearchParams({ page, limit, search });
  const resp = await api.get(`/kOjtx3AXNmCsT5Wz/assist-ai/quota-list?${qs}`);
  return resp.data;
};

const updateAssistAiQuota = async (userId, usedToday) => {
  const resp = await api.put(`/kOjtx3AXNmCsT5Wz/assist-ai/quota`, { userId, usedToday });
  return resp.data;
};

const resetAssistAiQuota = async (userId) => {
  const qs = new URLSearchParams({ userId });
  const resp = await api.delete(`/kOjtx3AXNmCsT5Wz/assist-ai/quota?${qs}`);
  return resp.data;
};

const getAssistAiUsageLogs = async (userId, params = {}) => {
  const { page = 1, limit = 30 } = params;
  const qs = new URLSearchParams({ userId, page, limit });
  const resp = await api.get(`/kOjtx3AXNmCsT5Wz/assist-ai/usage-logs?${qs}`);
  return resp.data;
};

const getAssistAiStats = async () => {
  const resp = await api.get(`/kOjtx3AXNmCsT5Wz/assist-ai/stats`);
  return resp.data;
};

const getAssistAiDailyLimitConfig = async () => {
  const resp = await api.get(`/kOjtx3AXNmCsT5Wz/assist-ai/daily-limit-config`);
  return resp.data;
};

const setAssistAiDailyLimit = async (dailyLimit) => {
  const resp = await api.post(`/kOjtx3AXNmCsT5Wz/assist-ai/daily-limit`, { dailyLimit });
  return resp.data;
};

// ============================================================
// 【网站主页悬浮窗配置】
// ============================================================

// 获取上传文件的基础URL（图片等静态资源）
// 开发环境: http://localhost:5000  生产环境: 同源（空字符串）
export const getFileBaseUrl = () => {
  if (API_BASE_URL.startsWith('http')) {
    return API_BASE_URL.replace(/\/api\/?$/, '');
  }
  return '';
};

// 拼接上传文件的完整访问地址
export const resolveFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${getFileBaseUrl()}${url}`;
};

// 获取悬浮窗配置（公开接口，无需认证）
const getFloatingWidgets = async () => {
  try {
    const resp = await api.get('/system-config/floating-widgets');
    return resp.data;
  } catch (error) {
    return { success: false, widgets: [] };
  }
};

// 保存悬浮窗配置（管理员接口）
const saveFloatingWidgets = async (widgets) => {
  const resp = await api.post('/system-config/floating-widgets', { widgets });
  return resp.data;
};

// 上传悬浮窗二维码图片（管理员接口）
const uploadFloatingWidgetImage = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  const resp = await api.post('/system-config/floating-widgets/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return resp.data;
};

// 获取客服微信号（公开接口）
const getServiceWechat = async () => {
  try {
    const resp = await api.get('/system-config/service-wechat');
    return resp.data;
  } catch (error) {
    return { success: false, wechatId: '' };
  }
};

// 创建API服务对象
const apiService = {
  ENABLE_MOCK_DATA,
  checkServerStatus,
  getUserInfo,
  getCaptcha,
  detectForgotAccount,
  sendForgotSmsCode,
  resetPasswordBySms,
  login,
  register,
  logout,
  getSystemStats,
  getAdminDashboardOverview,
  getAdminDashboardTrends,
  getAdminUserSegments,
  getAdminTopUsers,
  getAdminUserInsights,
  getAdminUserAnomalies,
  resetAllRewritingStatus,
  getAdminRechargeOrders,
  getAdminCardKeyRecharges,
  getAdminAllRechargeRecords,
  getAdminRechargeStats,
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  getCardKeys,
  getAllCardKeys,
  generateCardKeys,
  deleteCardKey,
  updateAfterRecharge,
  recharge,
  getUserRecharges,
  getUserTransactions,
  getUserConsumptions,
  optimizeChapterContent,
  optimizeChapterContentAsync,
  rewriteChapterContentSync,
  rewriteChapterContentAsync,
  refreshUserData,
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  // 支付宝当面付
  createAlipayOrder,
  queryAlipayOrder,
  cancelAlipayOrder,
  getAlipayPackages,
  forceLogoutUser,
  changeUserPassword,
  changePassword,
  updateProfile,
  getMyInviteCode,
  getInviteStats,
  getInviteList,
  verifyInviteCode,
  getAccessStatus,
  getAssistAiQuotaList,
  updateAssistAiQuota,
  resetAssistAiQuota,
  getAssistAiUsageLogs,
  getAssistAiStats,
  getAssistAiDailyLimitConfig,
  setAssistAiDailyLimit,
  getFloatingWidgets,
  saveFloatingWidgets,
  uploadFloatingWidgetImage,
  getServiceWechat
};

export default apiService; 
